import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-semibold transition-[colors,transform] duration-press ease-spring motion-safe:active:scale-[0.96] motion-reduce:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:        "bg-primary text-primary-foreground hover:bg-primary/90",
        primary:        "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80",
        secondary:      "bg-secondary text-secondary-foreground hover:bg-secondary/90",
        cta:            "bg-brand-red text-white hover:bg-brand-red/90 active:bg-brand-red/80",
        outline:        "border border-border bg-card text-foreground hover:bg-muted",
        ghost:          "bg-transparent text-foreground hover:bg-muted",
        "ghost-on-dark":"bg-transparent text-white hover:bg-white/10 border border-transparent",
        destructive:    "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        link:           "text-primary underline-offset-4 hover:underline",
      },
      size: {
        sm:      "h-9 px-4 text-sm",
        default: "h-11 px-5 text-sm min-h-[44px] touch-manipulation",
        lg:      "h-12 px-6 text-base",
        icon:    "h-11 w-11 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
